import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BookOpen, Mail, RefreshCw, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('EmailVerificationRequired')

// Rate limit cooldown in milliseconds (60 seconds)
// Note: This is client-side UX only; server-side rate limiting is enforced in /api/auth/send-verification-email
const RESEND_COOLDOWN_MS = 60000

const EmailVerificationRequired = () => {
  const [isResending, setIsResending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const hasCleanedUp = useRef(false)
  const location = useLocation()
  const email = location.state?.email || ''
  const justRegistered = location.state?.justRegistered || false

  // Clear the justRegistered state from history to prevent showing banner on refresh
  // Use ref to ensure cleanup only runs once even if dependencies change
  useEffect(() => {
    if (justRegistered && !hasCleanedUp.current) {
      window.history.replaceState({ email, justRegistered: false }, '')
      hasCleanedUp.current = true
    }
  }, [justRegistered, email])

  const handleResendEmail = async () => {
    if (!email) {
      toast.error('Email address not found. Please try logging in again.')
      return
    }

    // Rate limiting check
    const lastSent = sessionStorage.getItem('last_verification_email_sent')
    if (lastSent && Date.now() - parseInt(lastSent) < RESEND_COOLDOWN_MS) {
      const remaining = Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - parseInt(lastSent))) / 1000)
      toast.error(`Please wait ${remaining} seconds before requesting another email.`)
      return
    }

    setIsResending(true)

    try {
      const response = await api.post('/auth/send-verification-email', { email })

      sessionStorage.setItem('last_verification_email_sent', Date.now().toString())
      setEmailSent(true)
      toast.success('Verification email sent! Please check your inbox.')
    } catch (error) {
      logger.error('Resend verification email error:', error)
      const errorMessage = error.response?.data?.error || 'Failed to send verification email'
      toast.error(errorMessage)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Success Banner for Just-Registered Users */}
        {justRegistered && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
              Account Created!
            </h3>
            <p className="text-sm text-green-700 dark:text-green-300">
              One more step to get started
            </p>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Verify your email
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please verify your email address to continue
          </p>
        </div>

        {/* Email Display */}
        {email && (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Verification email sent to:
            </p>
            <p className="text-base font-medium text-gray-900 dark:text-white break-all">{email}</p>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-primary-600 dark:text-primary-400">1</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Check your email
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Look for the verification email in your inbox (and spam folder)
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-primary-600 dark:text-primary-400">2</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Click the verification link
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Open the email and click the verification link to activate your account
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Log in to your account
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Once verified, you can log in and access your account
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleResendEmail}
            disabled={isResending || emailSent}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : emailSent ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Email sent!
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Resend verification email
              </>
            )}
          </button>

          <Link
            to="/login"
            className="block w-full text-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
          >
            Back to login
          </Link>
        </div>

        {/* Help Text */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Didn't receive the email? Check your spam folder or click "Resend verification email"
            above.
          </p>
        </div>
      </div>
    </div>
  )
}

export default EmailVerificationRequired
